import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { PROFILE } from '../store/actionTypes';
import { Profile, ProfileInput, UserInput } from './models/profile';
import { User } from './models/authentication';

const profileQuery = `{
  userId
  firstname
  lastname
  ids {
    name
    value
  }
}`;

export const profile = createApi({
  reducerPath: PROFILE,
  baseQuery,
  tagTypes: ['Profile'],
  endpoints: builder => ({
    getProfile: builder.query<Profile, { userId: string }>({
      query: variables => ({
        document: gql`
          query Profile($userId: ObjectID!) {
            profile(userId: $userId) ${profileQuery}
          }`,
        variables,
      }),
      transformResponse: (response: { profile: Profile }) => response?.profile,
      providesTags: ['Profile'],
    }),
    updateProfile: builder.mutation<Profile, ProfileInput>({
      query: variables => ({
        document: gql`mutation UpdateProfile($userId: ObjectID!, $profileInput: ProfileInput!) {
          updateProfile(userId: $userId, profileInput: $profileInput) ${profileQuery}
          }`,
        variables,
      }),
      invalidatesTags: ['Profile'],
    }),
    getUser: builder.query<User, { userId: string }>({
      query: variables => ({
        document: gql`
          query User($userId: String!) {
            user(id: $userId) {
              email
              profile {
                firstname
                lastname
                ids {
                  name
                  value
                }
              }
              phone
              id
              preferences {
                paymentMethod
                defaultDeliveryContact
                notifications {
                  medium
                  status
                }
                id
              }
              roleId
              referralCode
              clientRequestToken
              emailVerified
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: { user: User }) => response?.user,
      providesTags: ['Profile'],
    }),
    updateUser: builder.mutation<User, UserInput>({
      query: variables => ({
        document: gql`
          mutation UpdateUser($userId: ObjectID!, $userInput: UserInput!) {
            updateUser(userId: $userId, userInput: $userInput) {
              preferences {
                paymentMethod
              }
              roleId
              email
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: { user: User }) => response?.user,
      invalidatesTags: ['Profile'],
    }),
  }),
});

export const {
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useUpdateUserMutation,
} = profile;
